import { useMemo } from 'react';
import reduce from 'lodash/reduce';

import { ICitySatellite } from '@r1-frontend/api-domru/api-content/v1/geography/get-cities-satellites';
import { IRegionsWithCitiesGroup, IRegionWithCities } from '@r1-frontend/api-domru/api-content/v1/geography/get-regions/dto/ResponseDto';

import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { useMatchMediaByWidth } from '@r1-frontend/shared/hooks/useMatchMedia';

interface IProps {
    regions: IRegionsWithCitiesGroup,
    topCities: ICitySatellite[],
}

export const useColumnsByRegionLetters = ({ regions, topCities }: IProps): IRegionsWithCitiesGroup[] => {
    const [isTablet, isLaptop, isDesktop] = [
        useMatchMediaByWidth(wideBreakpoints.mobile, true),
        useMatchMediaByWidth(wideBreakpoints.tablet, true),
        useMatchMediaByWidth(wideBreakpoints.laptop, true),
    ];

    const { firstColumnSize, columnsCount } = useMemo(() => {
        switch (true) {
            case isDesktop:
                return {
                    firstColumnSize: 6,
                    columnsCount: 4,
                };
            case isLaptop:
                return {
                    firstColumnSize: 8,
                    columnsCount: 3,
                };
            case isTablet:
                return {
                    firstColumnSize: 16,
                    columnsCount: 2,
                };
            default:
                return {
                    firstColumnSize: 0,
                    columnsCount: 1,
                };
        }
    }, [isDesktop, isLaptop, isTablet]);

    const regionsCount = reduce(regions, (result, value) => result + value.length, 0);

    return (() => {
        const otherColRegionSize = Math.round(regionsCount / columnsCount) +
            ((!!firstColumnSize && !!topCities.length) ? Math.round(firstColumnSize / columnsCount) : 0);

        const map = new Map<string, IRegionWithCities[]>();
        const result: IRegionsWithCitiesGroup[] = [];
        const regionLetters = Object.keys(regions);

        let currCol = 0;
        let currCount = 0;

        for (const letter of regionLetters) {
            const mid = currCount + regions[letter].length / 2;

            if (topCities.length && !!firstColumnSize && firstColumnSize < currCount && currCol === 0) {
                result.push(Object.fromEntries(map.entries()));
                currCount = 0;
                map.clear();
                currCol += 1;
            }

            if (otherColRegionSize < mid) {
                result.push(Object.fromEntries(map.entries()));
                currCount = 0;
                map.clear();
            }

            map.set(letter, regions[letter]);
            currCount += regions[letter].length;
        }

        if (map.size) {
            result.push(Object.fromEntries(map.entries()));
        }

        return result;
    })();
};
